import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Flex,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd';
import { CloseOutlined, TranslationOutlined } from '@ant-design/icons';
import { DocumentFrame, DocumentViewer } from '@/components/DocumentViewer';
import { VIEWER_BACKDROP } from '@/components/DocumentViewer';
import type { DocumentTranslation } from '@/pages/application/apis/applicationApi';

type Mode = 'original' | 'translated' | 'split';

interface Props {
  /** Names the file in the viewer and decides what each pane draws. */
  fileName?: string;
  /** Original document SAS URL (from the deal document list). */
  sasUrl: string | null;
  isGenerateSasUrlLoading: boolean;
  onClose: () => void;
  /** The translation denotion for this document, if any. */
  translation?: DocumentTranslation;
  /** Fetches the auth-guarded translated PDF as a blob object URL. */
  fetchTranslatedObjectUrl: (t: DocumentTranslation) => Promise<string | null>;
}

/**
 * Document preview with an Original / Translated / Side-by-side toggle. The
 * translated PDF lives behind an auth-guarded route, so it's fetched with the
 * bearer token into a blob object URL (revoked on cleanup) and embedded rather
 * than linked. Mounted per-document (keyed by id) so state + object URLs reset
 * cleanly when the selection changes.
 */
const TranslatedDocumentPreview = ({
  fileName,
  sasUrl,
  isGenerateSasUrlLoading,
  onClose,
  translation,
  fetchTranslatedObjectUrl,
}: Props) => {
  const { token } = theme.useToken();

  const hasTranslated =
    translation?.status === 'COMPLETED' && !!translation.translatedPdfUrl;

  const [mode, setMode] = useState<Mode>('original');
  const [translatedUrl, setTranslatedUrl] = useState<string | null>(null);
  const [loadingTranslated, setLoadingTranslated] = useState(false);
  // Guards a one-time fetch so toggling Original ⇄ Translated/Side-by-side
  // reuses the cached blob instead of re-downloading it.
  const fetchStartedRef = useRef(false);
  const objectUrlRef = useRef<string | null>(null);

  const needsTranslated = mode === 'translated' || mode === 'split';

  // Fetch the translated PDF once, the first time a translated view is opened,
  // then keep the object URL for the component's lifetime. The component is
  // keyed per-document, so switching documents remounts it and the unmount
  // cleanup below revokes the URL.
  useEffect(() => {
    if (!needsTranslated || !hasTranslated || !translation) return;
    if (fetchStartedRef.current) return; // already fetched / in flight
    fetchStartedRef.current = true;

    let cancelled = false;
    setLoadingTranslated(true);
    fetchTranslatedObjectUrl(translation)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        objectUrlRef.current = url;
        setTranslatedUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          fetchStartedRef.current = false; // allow a retry after a failure
          message.error('Failed to load translated PDF');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTranslated(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsTranslated, hasTranslated, translation, fetchTranslatedObjectUrl]);

  // Revoke the blob only when the component unmounts (document change / close).
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const toReview = translation?.lowConfidenceCount ?? 0;

  const toolbar = (
    <Flex
      align="center"
      gap={12}
      style={{
        padding: '10px 12px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        flexShrink: 0,
      }}
    >
      {/* Left — translation status */}
      <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0 }}>
        {hasTranslated && toReview > 0 && (
          <Tooltip title="Low-confidence fields — verify against the source">
            <Tag color="gold" bordered={false} style={{ margin: 0 }}>
              {toReview} to review
            </Tag>
          </Tooltip>
        )}
        {!hasTranslated && translation?.status === 'RUNNING' && (
          <Tag color="processing" bordered={false} style={{ margin: 0 }}>
            Translating…
          </Tag>
        )}
      </Flex>

      {/* Center — the view toggle */}
      <Segmented<Mode>
        shape="round"
        value={mode}
        onChange={setMode}
        options={[
          { label: 'Original', value: 'original' },
          {
            label: 'Translated',
            value: 'translated',
            disabled: !hasTranslated,
            icon: <TranslationOutlined />,
          },
          {
            label: 'Side by side',
            value: 'split',
            disabled: !hasTranslated,
          },
        ]}
      />

      {/* Right — close */}
      <Flex align="center" justify="flex-end" style={{ flex: 1, minWidth: 0 }}>
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
        >
          Close
        </Button>
      </Flex>
    </Flex>
  );

  /* Both panes draw through the shared frame, so the translated copy gets the
   * same treatment as the original - including the download fallback, which
   * matters because a translation of a spreadsheet is still a spreadsheet. */
  const translatedFrame = () => (
    <div
      style={{
        position: 'relative',
        height: '100%',
        background: VIEWER_BACKDROP,
      }}
    >
      {loadingTranslated && (
        <Flex
          align="center"
          justify="center"
          style={{ position: 'absolute', inset: 0, zIndex: 2 }}
        >
          <Space direction="vertical" align="center">
            <Spin />
            <Typography.Text style={{ color: '#fff', fontSize: 12 }}>
              Loading translation…
            </Typography.Text>
          </Space>
        </Flex>
      )}
      {translatedUrl ? (
        <DocumentFrame url={translatedUrl} fileName={fileName} />
      ) : (
        !loadingTranslated && (
          <Flex
            align="center"
            justify="center"
            style={{ height: '100%', padding: 24 }}
          >
            <Typography.Text style={{ color: '#fff', fontSize: 12 }}>
              Translated preview unavailable
            </Typography.Text>
          </Flex>
        )
      )}
    </div>
  );

  const originalFrame = () =>
    sasUrl ? (
      <div
        style={{
          position: 'relative',
          height: '100%',
          background: VIEWER_BACKDROP,
        }}
      >
        <DocumentFrame url={sasUrl} fileName={fileName} />
      </div>
    ) : (
      <Flex
        align="center"
        justify="center"
        style={{ height: '100%', background: VIEWER_BACKDROP, padding: 24 }}
      >
        <Typography.Text style={{ color: '#fff', fontSize: 12 }}>
          {isGenerateSasUrlLoading ? 'Fetching secure link…' : 'No preview'}
        </Typography.Text>
      </Flex>
    );

  const paneLabel = (text: string) => (
    <div
      style={{
        padding: '4px 10px',
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: token.colorTextSecondary,
        background: token.colorFillQuaternary,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        flexShrink: 0,
      }}
    >
      {text}
    </div>
  );

  return (
    <Flex vertical style={{ height: '100%' }}>
      {toolbar}
      <div style={{ flex: 1, minHeight: 0 }}>
        {mode === 'original' && (
          <DocumentViewer
            url={sasUrl}
            isLoading={isGenerateSasUrlLoading}
            fileName={fileName}
            // The toolbar above already carries a Close; the viewer adding a
            // second one would put two of them in the same corner.
            showDownload={false}
          />
        )}

        {mode === 'translated' && translatedFrame()}

        {mode === 'split' && (
          <Flex style={{ height: '100%' }}>
            <Flex
              vertical
              style={{
                flex: 1,
                minWidth: 0,
                borderRight: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              {paneLabel('Original')}
              <div style={{ flex: 1, minHeight: 0 }}>{originalFrame()}</div>
            </Flex>
            <Flex vertical style={{ flex: 1, minWidth: 0 }}>
              {paneLabel('Translated')}
              <div style={{ flex: 1, minHeight: 0 }}>{translatedFrame()}</div>
            </Flex>
          </Flex>
        )}
      </div>
    </Flex>
  );
};

export default TranslatedDocumentPreview;
